"use client";

/**
 * Espera full-screen de la consola (FEATURE-113): el loader de marca —isotipo que se
 * dibuja + barra de avance— en vez del spinner genérico que había antes.
 *
 * Es un envoltorio delgado de `LeagueLoader` y existe por una sola razón: ser el
 * **único** lugar donde la consola decide cómo se ve una espera de pantalla completa.
 * Lo montan el gate de sesión, el boot de la consola y el rebote de `/app/settings`; si
 * cada uno eligiera su modo y su mensaje, el paso de una pantalla a la otra volvería a
 * verse como un salto (que era justo el defecto).
 *
 * Las dos decisiones que fija:
 *
 * - **El modo lo elige quien mira** (ADR-008 §5.7). Acá se separa del boot del hincha,
 *   que es neutro oscuro a propósito: en la vitrina el tema que viene es el de la liga y
 *   el crossfade lo absorbe, pero en la consola el modo es una elección explícita del
 *   organizador y mandarle una pantalla completa en el modo contrario es exactamente lo
 *   que esa regla vino a evitar.
 * - **El acento NO es el de la liga**: la consola es nuestra herramienta, no la vitrina
 *   del cliente. Va el verde de marca, fijo — el mismo verde en los dos modos, en su
 *   calibración para pantalla o para papel. El boot público no tuvo que resolver esto
 *   porque siempre es oscuro; acá, con el lima sobre la piel clara, la barra de avance
 *   desaparecía.
 *
 * Queda un residuo conocido: `useModoTema` arranca en el default y aplica la preferencia
 * en un efecto (obligado por el static export; leer el storage en el render rompe la
 * hidratación). O sea, un frame oscuro antes del claro — el mismo que ya tiene
 * `AdminTheme`, no uno nuevo.
 */
import { useTranslations } from "next-intl";
import { LeagueLoader } from "@/components/league/primitives/LeagueLoader";
import { MARCA } from "@/components/ui/isotipo";
import { useModoTema, type ModoResuelto } from "@/hooks/useModoTema";

export function AdminLoader({
  message,
  variant = "fullscreen",
  mode,
}: {
  message?: string;
  /** `inline` es para la galería del sistema de diseño; el producto usa el default. */
  variant?: "fullscreen" | "inline";
  /**
   * Solo la galería lo pasa: ahí el modo es el del interruptor de la lámina, no el
   * guardado del organizador, y sin este override la muestra quedaría inerte al cambio
   * de tema (que es una de las cosas que la auditoría de `/design` mide).
   */
  mode?: ModoResuelto;
}) {
  const t = useTranslations("admin");
  const { resuelto } = useModoTema();
  const modo = mode ?? resuelto;

  return (
    <LeagueLoader
      mode={modo}
      accent={modo === "light" ? MARCA.verdeClaro : MARCA.verde}
      message={message ?? t("loading")}
      variant={variant}
    />
  );
}
